'use client';

import { Breadcrumbs as MuiBreadcrumbs, Typography } from '@mui/material';
import Link from 'next/link';
import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { COLORS } from '@/constants';
import { normalizeHTML } from '@/common';
import { Container } from './styles';

type BreadcrumbsProps = {
  title?: string;
  breadcrumbsTitle?: string;
  backHref?: string;
  backLabel?: string;
};

const truncatedTitleSx = {
  maxWidth: { xs: '200px', sm: '400px', md: '700px' },
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const Breadcrumbs = ({ title, breadcrumbsTitle, backHref, backLabel }: BreadcrumbsProps) => {
  const t = useTranslations();
  const pathname = usePathname();
  const locale = useLocale();

  const pathSegments = useMemo(() => {
    return pathname ? pathname.split('/').filter(Boolean) : [];
  }, [pathname]);

  const normalizedTitle = useMemo(() => (title ? normalizeHTML(title) : undefined), [title]);
  const normalizedBreadcrumbsTitle = useMemo(
    () => (breadcrumbsTitle ? normalizeHTML(breadcrumbsTitle) : undefined),
    [breadcrumbsTitle],
  );

  const breadcrumbsElements = useMemo(() => {
    const elements = [
      <Link key="home" href="/">
        <Typography color={COLORS['primary/600']}>{t('navigation.home')}</Typography>
      </Link>,
    ];

    const displayedTitle = normalizedBreadcrumbsTitle || normalizedTitle;

    if (backHref && backLabel) {
      elements.push(
        <Link key="back" href={backHref}>
          <Typography color={COLORS['primary/600']}>{backLabel}</Typography>
        </Link>,
      );
      elements.push(
        <Typography key="current" noWrap sx={truncatedTitleSx}>
          {displayedTitle}
        </Typography>,
      );
      return elements;
    }

    const nonLocaleSegments = pathSegments
      .map((segment, originalIndex) => ({ segment, originalIndex }))
      .filter(({ segment, originalIndex }) => !(originalIndex === 0 && segment === locale));

    if (nonLocaleSegments.length === 0) {
      elements.push(<Typography key="current">{displayedTitle}</Typography>);
      return elements;
    }

    nonLocaleSegments.forEach(({ segment, originalIndex }, index) => {
      const isLast = index === nonLocaleSegments.length - 1;
      const href = '/' + pathSegments.slice(0, originalIndex + 1).join('/');

      if (isLast) {
        elements.push(
          <Typography key={`segment-${index}`} noWrap sx={truncatedTitleSx}>
            {displayedTitle}
          </Typography>,
        );
      } else {
        elements.push(
          <Link key={`segment-${index}`} href={href}>
            <Typography color={COLORS['primary/600']}>{t(`navigation.${segment}`)}</Typography>
          </Link>,
        );
      }
    });

    return elements;
  }, [pathSegments, locale, normalizedTitle, normalizedBreadcrumbsTitle, t]);

  return (
    <Container $withTitle={!!normalizedTitle}>
      <MuiBreadcrumbs aria-label="breadcrumb" suppressHydrationWarning>
        {breadcrumbsElements}
      </MuiBreadcrumbs>
      {normalizedTitle && (
        <Typography
          fontWeight={700}
          fontSize={{ xs: 24, sm: 30 }}
          lineHeight="120%"
          sx={{ wordBreak: 'break-word' }}
        >
          {normalizedTitle}
        </Typography>
      )}
    </Container>
  );
};
